// frontend/src/components/Products/BulkImport.jsx
import React, { useState } from 'react';
import { productService } from '../../services/api';  // ✅ Change to api.js instead of products.js
import { toast } from 'react-hot-toast';
import { DocumentArrowDownIcon, ArrowUpTrayIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export default function BulkImport({ onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [step, setStep] = useState('upload'); // upload, preview, confirm, results

  // Download template
  const downloadTemplate = () => {
    const headers = [
      'sku', 'name', 'description', 'category',
      'price', 'cost_price', 'tax_rate',
      'current_stock', 'min_stock_level', 'barcode'
    ];
    
    const sampleRow = {
      sku: 'PROD-001',
      name: 'Sample Product',
      description: 'Product description here',
      category: 'Electronics',
      price: '2500',
      cost_price: '2000',
      tax_rate: '16',
      current_stock: '100',
      min_stock_level: '10',
      barcode: '1234567890123'
    };
    
    const csvRows = [];
    csvRows.push(headers.join(','));
    
    const values = headers.map(header => {
      const value = sampleRow[header] || '';
      return `"${value}"`;
    });
    csvRows.push(values.join(','));
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', 'brominon_product_import_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Template downloaded successfully');
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.csv') && !selectedFile.name.endsWith('.xlsx')) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    setFile(selectedFile);
    previewFile(selectedFile);
  };

  const previewFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      const lines = content.split('\n');
      
      // Filter out empty lines
      const dataLines = lines.filter(line => line.trim() && !line.trim().startsWith('#'));
      
      if (dataLines.length < 2) {
        toast.error('File appears to be empty');
        return;
      }
      
      const headers = dataLines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const requiredFields = ['sku', 'name', 'price'];
      const missingRequired = requiredFields.filter(f => !headers.includes(f));
      
      if (missingRequired.length > 0) {
        toast.error(`Missing required columns: ${missingRequired.join(', ')}`);
        return;
      }
      
      // Parse data rows
      const products = [];
      const errors = [];
      
      for (let i = 1; i < dataLines.length; i++) {
        const values = parseCSVLine(dataLines[i]);
        if (values.length === 0) continue;
        
        const product = {};
        headers.forEach((header, idx) => {
          let value = values[idx] ? values[idx].replace(/"/g, '').trim() : '';
          product[header] = value;
        });
        
        // Validate required fields
        const skuError = !product.sku ? 'SKU is required' : null;
        const nameError = !product.name ? 'Product name is required' : null;
        const priceError = !product.price || isNaN(parseFloat(product.price)) 
          ? 'Valid price is required' : null;
        
        if (skuError || nameError || priceError) {
          errors.push({
            row: i + 1,
            sku: product.sku || 'MISSING',
            errors: [skuError, nameError, priceError].filter(e => e)
          });
        } else {
          products.push({
            sku: product.sku,
            name: product.name,
            description: product.description || '',
            category: product.category || '',
            unit_price: parseFloat(product.price),
            cost_price: product.cost_price ? parseFloat(product.cost_price) : 0,
            tax_rate: product.tax_rate ? parseFloat(product.tax_rate) : 16,
            current_stock: product.current_stock ? parseInt(product.current_stock) : 0,
            min_stock_level: product.min_stock_level ? parseInt(product.min_stock_level) : 0,
            barcode: product.barcode || null
          });
        }
      }
      
      setPreview(products);
      setValidationErrors(errors);
      
      if (errors.length > 0) {
        setStep('preview');
        toast.warning(`${errors.length} product(s) have validation errors`);
      } else if (products.length > 0) {
        setStep('preview');
        toast.success(`${products.length} product(s) ready to import`);
      } else {
        toast.error('No valid products found in file');
      }
    };
    
    reader.readAsText(file);
  };

  const parseCSVLine = (line) => {
    const result = [];
    let inQuote = false;
    let current = '';
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuote = !inQuote;
      } else if (char === ',' && !inQuote) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const handleImport = async () => {
    if (preview.length === 0) {
      toast.error('No products to import');
      return;
    }
    
    setUploading(true);
    try {
      const response = await productService.bulkImport({ products: preview });
      setImportResults(response.results);
      setStep('results');
      
      const successCount = response.results.success.length;
      const failedCount = response.results.failed.length;
      
      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} products`);
      }
      if (failedCount > 0) {
        toast.error(`Failed to import ${failedCount} products`);
      }
      
      if (successCount > 0) {
        onSuccess();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.error || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setPreview([]);
    setValidationErrors([]);
    setImportResults(null);
    setStep('upload');
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div>
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Required columns:</strong> sku, name, price (without VAT)
            </p>
            <p className="text-sm text-blue-700 mt-1">
              <strong>Optional:</strong> description, category, cost_price, tax_rate, current_stock, min_stock_level, barcode
            </p>
          </div>
          
          <div className="flex justify-end mb-4">
            <button
              onClick={downloadTemplate}
              className="flex items-center px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
              Download Template
            </button>
          </div>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer inline-flex flex-col items-center"
            >
              <ArrowUpTrayIcon className="h-12 w-12 text-gray-400 mb-3" />
              <span className="text-gray-600">Click to upload CSV file</span>
              <span className="text-xs text-gray-400 mt-1">CSV format only</span>
            </label>
          </div>
        </div>
      )}
      
      {/* Step 2: Preview */}
      {step === 'preview' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Preview Import</h3>
            <button
              onClick={resetImport}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Upload Different File
            </button>
          </div>
          
          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-semibold text-red-800 text-sm flex items-center">
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                {validationErrors.length} Product(s) Have Errors
              </h4>
              <div className="mt-1 max-h-32 overflow-y-auto">
                {validationErrors.slice(0, 5).map((err, idx) => (
                  <div key={idx} className="text-xs text-red-700 mt-1">
                    <strong>Row {err.row}:</strong> {err.errors.join(', ')}
                  </div>
                ))}
                {validationErrors.length > 5 && (
                  <p className="text-xs text-red-600 mt-1">+ {validationErrors.length - 5} more errors</p>
                )}
              </div>
            </div>
          )}
          
          {/* Valid Products Preview */}
          {preview.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold text-gray-900 text-sm mb-2">
                {preview.length} Valid Product(s) Ready to Import
              </h4>
              <div className="max-h-64 overflow-y-auto border rounded-lg">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">SKU</th>
                      <th className="px-2 py-1 text-left">Name</th>
                      <th className="px-2 py-1 text-right">Price</th>
                      <th className="px-2 py-1 text-center">Tax</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {preview.slice(0, 10).map((product, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-2 py-1">{product.sku}</td>
                        <td className="px-2 py-1">{product.name}</td>
                        <td className="px-2 py-1 text-right">KES {product.unit_price.toLocaleString()}</td>
                        <td className="px-2 py-1 text-center">{product.tax_rate || 16}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.length > 10 && (
                  <p className="text-xs text-gray-500 p-1 text-center">
                    + {preview.length - 10} more products
                  </p>
                )}
              </div>
            </div>
          )}
          
          <div className="flex justify-end space-x-2">
            <button
              onClick={resetImport}
              className="px-3 py-1.5 bg-gray-200 text-sm rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={preview.length === 0 || uploading}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? 'Importing...' : `Import ${preview.length} Products`}
            </button>
          </div>
        </div>
      )}
      
      {/* Step 3: Results */}
      {step === 'results' && importResults && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Import Results</h3>
          
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <CheckCircleIcon className="h-6 w-6 text-green-600 mx-auto mb-1" />
              <div className="text-xl font-bold text-green-600">{importResults.success.length}</div>
              <div className="text-xs text-gray-600">Imported</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <XCircleIcon className="h-6 w-6 text-red-600 mx-auto mb-1" />
              <div className="text-xl font-bold text-red-600">{importResults.failed.length}</div>
              <div className="text-xs text-gray-600">Failed</div>
            </div>
          </div>
          
          {importResults.failed.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold text-red-800 text-sm mb-1">Failed Items</h4>
              <div className="max-h-32 overflow-y-auto bg-red-50 p-2 rounded-lg text-xs">
                {importResults.failed.slice(0, 5).map((fail, idx) => (
                  <div key={idx} className="text-red-700">
                    <strong>{fail.sku || 'Unknown'}:</strong> {fail.error}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex justify-end">
            <button
              onClick={() => {
                resetImport();
                onSuccess();
              }}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}